import React, {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import moment from 'moment';
import ErrorBoundary from './ErrorBoundary';
import { getMoodColor } from './diaryStats';
import ManualEntryEditor from './ManualEntryEditor';
import PlaceTagsEditor from './PlaceTagsEditor';
import PrintSummaryView from './PrintSummaryView';
import ReplayControls from './ReplayControls';
import { t } from './i18n';

const ImportModal = lazy(() => import('./ImportModal'));
const DiaryStatsModal = lazy(() => import('./DiaryStatsModal'));
const GitHubContributionsModal = lazy(() => import('./GitHubContributionsModal'));
const DashboardInsights = lazy(() => import('./DashboardInsights'));
const TripCard = lazy(() => import('./TripCard'));
const AnniversaryModal = lazy(() => import('./AnniversaryModal'));
const MemoryBookModal = lazy(() => import('./MemoryBookModal'));

export const MODAL_LOADING_FALLBACK = (
  <div className="diary-modal-overlay" role="status" aria-label={t('web.modal.loading')}>
    <div className="spinner" />
  </div>
);

const ModalsContext = createContext(null);

export function useModalsState() {
  const [isAnniversaryOpen, setIsAnniversaryOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [pendingManualForm, setPendingManualForm] = useState(false);
  const [pendingImportFocus, setPendingImportFocus] = useState(null);
  const [isDiaryStatsOpen, setIsDiaryStatsOpen] = useState(false);
  const [isGithubOpen, setIsGithubOpen] = useState(false);
  const [isMemoryBookOpen, setIsMemoryBookOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [selectedDiaryEntry, setSelectedDiaryEntry] = useState(null);
  const [editingManualEntry, setEditingManualEntry] = useState(null);
  const [selectedConcert, setSelectedConcert] = useState(null);
  const [selectedEntertainment, setSelectedEntertainment] = useState(null);
  const [selectedMusicDay, setSelectedMusicDay] = useState(null);
  const [selectedPlaceForTags, setSelectedPlaceForTags] = useState(null);
  const [selectedDwellBin, setSelectedDwellBin] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [printSummaryRequest, setPrintSummaryRequest] = useState(null);

  const openImport = useCallback((manualForm = false, focusSection = null) => {
    setPendingManualForm(manualForm);
    setPendingImportFocus(focusSection || null);
    setIsImportModalOpen(true);
  }, []);

  const closeImport = useCallback(() => {
    setIsImportModalOpen(false);
    setPendingManualForm(false);
    setPendingImportFocus(null);
  }, []);

  const openQuickAdd = useCallback(() => {
    setPendingManualForm(true);
    setPendingImportFocus(null);
    setIsImportModalOpen(true);
  }, []);

  return {
    isAnniversaryOpen,
    setIsAnniversaryOpen,
    isImportModalOpen,
    setIsImportModalOpen,
    pendingManualForm,
    setPendingManualForm,
    pendingImportFocus,
    setPendingImportFocus,
    isDiaryStatsOpen,
    setIsDiaryStatsOpen,
    isGithubOpen,
    setIsGithubOpen,
    isMemoryBookOpen,
    setIsMemoryBookOpen,
    isDashboardOpen,
    setIsDashboardOpen,
    selectedDiaryEntry,
    setSelectedDiaryEntry,
    editingManualEntry,
    setEditingManualEntry,
    selectedConcert,
    setSelectedConcert,
    selectedEntertainment,
    setSelectedEntertainment,
    selectedMusicDay,
    setSelectedMusicDay,
    selectedPlaceForTags,
    setSelectedPlaceForTags,
    selectedDwellBin,
    setSelectedDwellBin,
    selectedTrip,
    setSelectedTrip,
    printSummaryRequest,
    setPrintSummaryRequest,
    openImport,
    closeImport,
    openQuickAdd,
  };
}

export function ModalsProvider({ children }) {
  const modals = useModalsState();
  const value = useMemo(() => modals, [modals]);
  return (
    <ModalsContext.Provider value={value}>
      {children}
    </ModalsContext.Provider>
  );
}

export function useModals() {
  const ctx = useContext(ModalsContext);
  if (!ctx) {
    throw new Error('useModals must be used within ModalsProvider');
  }
  return ctx;
}

export function AppModals({
  modals,
  user,
  safeFetchedData,
  currentFilters,
  fetchDataFromBackend,
  handleLogout,
  insights,
  insightsLoadedData,
  homeBase,
  handleDashboardCardAction,
  handlePrintPeriodSummary,
  handleShareTrip,
  handlePrintTrip,
  calendarDate,
  anniversaryItems,
  handleAnniversarySelect,
  handleAnniversaryDateChange,
  cityReplay,
  setCityReplay,
  replayIndex,
  replayPlaying,
  replaySpeed,
  setReplaySpeed,
  handleReplayPlay,
  handleReplayPause,
  handleReplayRestart,
  handleReplayExport,
  replayExportState,
  replayExportError,
}) {
  const {
    isAnniversaryOpen,
    setIsAnniversaryOpen,
    isImportModalOpen,
    pendingManualForm,
    pendingImportFocus,
    setPendingImportFocus,
    closeImport,
    isDiaryStatsOpen,
    setIsDiaryStatsOpen,
    isGithubOpen,
    setIsGithubOpen,
    isMemoryBookOpen,
    setIsMemoryBookOpen,
    isDashboardOpen,
    setIsDashboardOpen,
    selectedDiaryEntry,
    setSelectedDiaryEntry,
    editingManualEntry,
    setEditingManualEntry,
    selectedConcert,
    setSelectedConcert,
    selectedEntertainment,
    setSelectedEntertainment,
    selectedMusicDay,
    setSelectedMusicDay,
    selectedPlaceForTags,
    setSelectedPlaceForTags,
    selectedDwellBin,
    setSelectedDwellBin,
    selectedTrip,
    setSelectedTrip,
    printSummaryRequest,
    setPrintSummaryRequest,
  } = modals;

  return (
    <ErrorBoundary
      title={t('web.modal.error.title')}
      message={t('web.modal.error.message')}
    >
      <>
        {selectedDiaryEntry && (
          <div className="diary-modal-overlay" onClick={() => setSelectedDiaryEntry(null)}>
            <div className="diary-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="diary-modal-close" onClick={() => setSelectedDiaryEntry(null)}>×</button>
              <h3 className="diary-modal-title" style={{ color: getMoodColor(selectedDiaryEntry.mood) }}>
                {selectedDiaryEntry.mood ? `${selectedDiaryEntry.mood} ` : ''}
                {selectedDiaryEntry.note_title || t('label.diary')}
              </h3>
              <div className="diary-modal-meta">
                <span className="diary-meta-item">
                  🗓️ {moment(selectedDiaryEntry.entry_date).format('DD/MM/YYYY')}
                </span>
                {selectedDiaryEntry.entry_time && (
                  <span className="diary-meta-item">
                    ⏰ {selectedDiaryEntry.entry_time}
                  </span>
                )}
              </div>

              {selectedDiaryEntry.activities && (
                <div className="diary-modal-section">
                  <h4>Atividades</h4>
                  <div className="diary-activities">
                    {selectedDiaryEntry.activities.split('|').map((act, i) => (
                      <span key={i} className="diary-activity-pill">{act.trim()}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedDiaryEntry.note && (
                <div className="diary-modal-section">
                  <h4>Nota</h4>
                  <p className="diary-note-text">{selectedDiaryEntry.note}</p>
                </div>
              )}
              {selectedDiaryEntry.source === 'manual' && selectedDiaryEntry.id != null && (
                <div className="manual-entry-editor-actions">
                  <button
                    type="button"
                    className="av-btn av-btn--primary"
                    onClick={() => {
                      setEditingManualEntry(selectedDiaryEntry);
                      setSelectedDiaryEntry(null);
                    }}
                  >
                    Editar entrada
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {editingManualEntry && (
          <ManualEntryEditor
            entry={editingManualEntry}
            onClose={() => setEditingManualEntry(null)}
            onSaved={async () => {
              await fetchDataFromBackend(currentFilters);
              setEditingManualEntry(null);
            }}
          />
        )}

        {selectedConcert && (
          <div className="diary-modal-overlay" onClick={() => setSelectedConcert(null)}>
            <div className="diary-modal-content concert-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="diary-modal-close" onClick={() => setSelectedConcert(null)}>×</button>
              <h3 className="diary-modal-title concert-modal-title">
                {selectedConcert.artist_name || 'Show'}
              </h3>
              <div className="diary-modal-meta concert-modal-meta">
                <span className="diary-meta-item">
                  Data: {moment(selectedConcert.event_date).format('DD/MM/YYYY')}
                </span>
                {selectedConcert.song_count > 0 && (
                  <span className="diary-meta-item">
                    {selectedConcert.song_count} músicas
                  </span>
                )}
              </div>

              <div className="diary-modal-section">
                <h4>Local</h4>
                <p className="concert-detail-text">
                  {[selectedConcert.venue_name, selectedConcert.city_name, selectedConcert.country_name]
                    .filter(Boolean)
                    .join(', ') || t('web.modal.placeUnknown')}
                </p>
              </div>

              {selectedConcert.tour_name && (
                <div className="diary-modal-section">
                  <h4>Turnê</h4>
                  <p className="concert-detail-text">{selectedConcert.tour_name}</p>
                </div>
              )}

              {selectedConcert.info && (
                <div className="diary-modal-section">
                  <h4>Info</h4>
                  <p className="diary-note-text">{selectedConcert.info}</p>
                </div>
              )}

              {selectedConcert.url && (
                <a
                  className="concert-setlist-link"
                  href={selectedConcert.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir no setlist.fm
                </a>
              )}
            </div>
          </div>
        )}

        {selectedEntertainment && (
          <div className="diary-modal-overlay" onClick={() => setSelectedEntertainment(null)}>
            <div className="diary-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="diary-modal-close" onClick={() => setSelectedEntertainment(null)}>×</button>
              {selectedEntertainment.type === 'movie' ? (
                <>
                  <h3 className="diary-modal-title">
                    🎬 {selectedEntertainment.data.title}
                    {selectedEntertainment.data.year ? ` (${selectedEntertainment.data.year})` : ''}
                  </h3>
                  <div className="diary-modal-meta">
                    <span className="diary-meta-item">
                      🗓️ {moment(selectedEntertainment.data.watched_at).format('DD/MM/YYYY')}
                    </span>
                    <span className="diary-meta-item">Filme · Trakt</span>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="diary-modal-title">
                    📺 {selectedEntertainment.data.show_title}
                  </h3>
                  <div className="diary-modal-meta">
                    <span className="diary-meta-item">
                      T{String(selectedEntertainment.data.season_number).padStart(2, '0')}E{String(selectedEntertainment.data.episode_number).padStart(2, '0')}
                    </span>
                    <span className="diary-meta-item">
                      🗓️ {moment(selectedEntertainment.data.watched_at).format('DD/MM/YYYY')}
                    </span>
                  </div>
                  {selectedEntertainment.data.episode_title && (
                    <div className="diary-modal-section">
                      <h4>Episódio</h4>
                      <p className="concert-detail-text">{selectedEntertainment.data.episode_title}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {selectedMusicDay && (
          <div className="diary-modal-overlay" onClick={() => setSelectedMusicDay(null)}>
            <div className="diary-modal-content music-day-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="diary-modal-close" onClick={() => setSelectedMusicDay(null)}>×</button>
              <h3
                className="diary-modal-title"
                style={{ color: selectedMusicDay.type === 'spotify' ? '#1db954' : '#fc3c44' }}
              >
                {selectedMusicDay.type === 'spotify' ? '🎧' : '🎵'} {selectedMusicDay.count === 1 ? t('web.modal.musicCount', { count: selectedMusicDay.count }) : t('web.modal.musicCountPlural', { count: selectedMusicDay.count })}
              </h3>
              <div className="diary-modal-meta">
                <span className="diary-meta-item">{selectedMusicDay.service}</span>
                <span className="diary-meta-item">
                  🗓️ {moment(selectedMusicDay.date).format('DD/MM/YYYY')}
                </span>
              </div>

              <div className="music-day-list">
                {(selectedMusicDay.plays || []).map((play, i) => (
                  <div key={`${play.played_at}-${i}`} className="music-day-row">
                    <span className="music-day-time">
                      {play.played_at ? moment(play.played_at).format('HH:mm') : '--:--'}
                    </span>
                    <span className="music-day-main">
                      <strong>{play.track_name}</strong>
                      <small>
                        {[play.artist_name, play.album_name].filter(Boolean).join(' · ') || '—'}
                      </small>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Suspense fallback={MODAL_LOADING_FALLBACK}>
          {isImportModalOpen ? (
            <ImportModal
              onClose={closeImport}
              user={user}
              onReload={() => fetchDataFromBackend(currentFilters)}
              onLogout={handleLogout}
              initialManualForm={pendingManualForm}
              initialFocusSection={pendingImportFocus}
              onFocusSectionConsumed={() => setPendingImportFocus(null)}
            />
          ) : null}

          {isDiaryStatsOpen ? (
            <DiaryStatsModal
              entries={safeFetchedData.diary}
              onClose={() => setIsDiaryStatsOpen(false)}
            />
          ) : null}

          {isGithubOpen ? (
            <GitHubContributionsModal
              user={user}
              onClose={() => setIsGithubOpen(false)}
            />
          ) : null}

          {isMemoryBookOpen ? (
            <MemoryBookModal
              data={safeFetchedData}
              user={user}
              onClose={() => setIsMemoryBookOpen(false)}
            />
          ) : null}

          {selectedPlaceForTags ? (
            <PlaceTagsEditor
              venueId={selectedPlaceForTags.id}
              venueName={selectedPlaceForTags.name}
              onClose={() => setSelectedPlaceForTags(null)}
            />
          ) : null}

          {selectedDwellBin ? (
            <div className="dwell-bin-overlay" onClick={() => setSelectedDwellBin(null)}>
              <div className="dwell-bin-card glass-panel" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="import-close-btn" onClick={() => setSelectedDwellBin(null)} aria-label={t('action.close')}>×</button>
                <h4>Tempo gasto nesta área</h4>
                <p className="dwell-bin-minutes">
                  {Math.floor(selectedDwellBin.totalMinutes / 60)}h {selectedDwellBin.totalMinutes % 60}min
                </p>
                <p>{selectedDwellBin.count} sinais registrados</p>
                {selectedDwellBin.firstDate && (
                  <p>De {selectedDwellBin.firstDate} a {selectedDwellBin.lastDate}</p>
                )}
              </div>
            </div>
          ) : null}

          {isDashboardOpen ? (
            <DashboardInsights
              insights={{
                ...insights,
                loadedData: insightsLoadedData,
                baseCity: homeBase.city,
                currentRange: {
                  startDate: currentFilters.startDate,
                  endDate: currentFilters.endDate,
                },
              }}
              onClose={() => setIsDashboardOpen(false)}
              onCardAction={handleDashboardCardAction}
              onPrintSummary={handlePrintPeriodSummary}
            />
          ) : null}

          {selectedTrip ? (
            <TripCard
              trip={selectedTrip}
              onClose={() => setSelectedTrip(null)}
              onShare={handleShareTrip}
              onPrint={handlePrintTrip}
            />
          ) : null}

          {printSummaryRequest ? (
            <PrintSummaryView
              data={safeFetchedData}
              range={printSummaryRequest.range}
              title={printSummaryRequest.title}
              onClose={() => setPrintSummaryRequest(null)}
            />
          ) : null}

          {isAnniversaryOpen ? (
            <AnniversaryModal
              selectedDate={calendarDate}
              items={anniversaryItems}
              onClose={() => setIsAnniversaryOpen(false)}
              onSelectItem={handleAnniversarySelect}
              onChangeDate={handleAnniversaryDateChange}
            />
          ) : null}

          {cityReplay && (
            <ReplayControls
              cityName={cityReplay.city}
              totalEvents={cityReplay.timeline.length}
              currentIndex={replayIndex}
              currentDate={cityReplay.timeline[replayIndex - 1]?.date || null}
              isPlaying={replayPlaying}
              speed={replaySpeed}
              progress={cityReplay.timeline.length > 0 ? (replayIndex / cityReplay.timeline.length) * 100 : 0}
              onPlay={handleReplayPlay}
              onPause={handleReplayPause}
              onSpeedChange={setReplaySpeed}
              onRestart={handleReplayRestart}
              onExport={handleReplayExport}
              exportState={replayExportState}
              exportError={replayExportError}
              onClose={() => {
                handleReplayRestart();
                setCityReplay(null);
              }}
            />
          )}
        </Suspense>
      </>
    </ErrorBoundary>
  );
}
